import { ParamOrganizationSlug, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { assertDashboardWithinProjectConstraint, formatDashboardDetails, resolveDashboardId, resolveDashboardProjectConstraint } from "./dashboards-BCwvH2V2.js";
import { z } from "zod";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/get-dashboard-details.ts
var get_dashboard_details_default = defineTool({
	name: "get_dashboard_details",
	skills: ["inspect"],
	requiredScopes: ["org:read"],
	description: [
		"Get detailed information about a specific Sentry dashboard.",
		"",
		"Use this tool when you need to:",
		"- Inspect a dashboard's widgets and saved query definitions",
		"- View dashboard projects, environments, filters, layout, and widget IDs",
		"- Resolve a dashboard by exact title or numeric ID",
		"",
		"<examples>",
		"get_dashboard_details(organizationSlug='my-organization', dashboardIdOrTitle='12345')",
		"get_dashboard_details(organizationSlug='my-organization', dashboardIdOrTitle='Errors Overview')",
		"</examples>",
		"",
		"<hints>",
		"- Numeric dashboard IDs are resolved directly.",
		"- Title lookups require one exact case-insensitive match. Use `find_dashboards` first if uncertain.",
		"- This returns saved widget query definitions, not live widget data.",
		"</hints>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug,
		regionUrl: ParamRegionUrl.nullable().default(null),
		dashboardIdOrTitle: z.string().trim().min(1).describe("The dashboard's numeric ID or exact title.")
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		const organizationSlug = params.organizationSlug;
		setTag("organization.slug", organizationSlug);
		const scopedProject = await resolveDashboardProjectConstraint({
			apiService,
			organizationSlug,
			scopedProjectSlug: context.constraints.projectSlug
		});
		if (scopedProject) {
			setTag("project.slug", scopedProject.slug);
			setTag("project.id", String(scopedProject.id));
		}
		const dashboardId = await resolveDashboardId({
			apiService,
			organizationSlug,
			dashboardIdOrTitle: params.dashboardIdOrTitle,
			projectId: scopedProject?.id
		});
		const dashboard = await apiService.getDashboard({
			organizationSlug,
			dashboardId
		});
		assertDashboardWithinProjectConstraint({
			dashboard,
			scopedProjectSlug: context.constraints.projectSlug,
			projectId: scopedProject?.id
		});
		return formatDashboardDetails({
			dashboard,
			organizationSlug,
			dashboardUrl: apiService.getDashboardUrl(organizationSlug, String(dashboard.id), {
				projectId: scopedProject?.id ?? (dashboard.projects.length === 1 ? dashboard.projects[0] : null),
				statsPeriod: dashboard.period ?? null
			})
		});
	}
});

//#endregion
export { get_dashboard_details_default };
//# sourceMappingURL=get-dashboard-details-7tf-4Q_F.js.map